import { Card } from '@material-ui/core'
import i18n from 'i18next'
import { sumBy } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import { PenaltyScheme } from 'src/api/gen/api'
import { getJurisdictionTimezone, stringToZonedDateTime } from 'src/commons/temporalHelpers'
import { DeleteButton } from 'src/components/Button'
import { useProviders } from 'src/layout/hooks'
import { CurrencyIntegerField } from 'src/pages/Policies/Editor/fields'

import { IntegerField } from '../Editor/fields'

import { CardSection } from './Editor'
import { PeriodField, PoliciesField, ProviderField } from './Editor/fields'
import FeeSummaryAccordion, { IProviderInfo } from './FeeSummaryAccordion'
import { useFeesSummaryPage } from './FeeSummaryPageHooks'

interface IFeesSummaryProps {
  archiveScheme: () => void
  fees_scheme: PenaltyScheme
}

const FeeSummaryPageList = function (props: IFeesSummaryProps) {
  const { t } = useTranslation()
  const timezone = getJurisdictionTimezone()
  const { activeProviders } = useProviders()

  const { loading, providerInfos } = useFeesSummaryPage({ fees_scheme: props.fees_scheme })

  async function fetchExportData(providerIds: string[]) {
    await api.fees.feesExport({
      provider_ids: providerIds,
      policy_start_date: props.fees_scheme.start_date,
      policy_end_date: props.fees_scheme.end_date,
      policy_ids: props.fees_scheme.policies_names.map((policy) => policy.id),
      // Export dates in the timezone and language of the user
      tz: timezone,
      lang: i18n.language,
    })
  }

  const getProviderOnClickExport = function (providerId: string) {
    return async function () {
      await fetchExportData([providerId])
    }
  }

  return (
    <>
      <Card style={{ overflow: 'visible', borderRadius: 'inherit', marginBottom: 10 }}>
        <CardSection
          title={t('titles.penalty_scheme.list.fees')}
          xs={2}
          button={<DeleteButton label={t('buttons.reset')} onClick={props.archiveScheme} />}
        >
          <ProviderField
            title={t('titles.providers')}
            handleChange={console.error}
            validation={{ isFieldValid: () => true, getFieldErrorMsg: () => '' } as any}
            disabled
            providers={activeProviders}
            value={props.fees_scheme.providers}
            withSelectAll={false}
            oneLineSelect={false}
          />
          <PoliciesField
            title={t('titles.fee_rates')}
            handleChange={console.error}
            validation={{ isFieldValid: () => true, getFieldErrorMsg: () => '' } as any}
            disabled
            allPolicies={props.fees_scheme.policies_names}
            value={props.fees_scheme.policies_names.map((policy) => policy.id)}
            withSelectAll={false}
            oneLineSelect={false}
          />
          <IntegerField
            title={t('titles.penalties_count')}
            handleChange={console.error}
            validation={{ isFieldValid: () => true, getFieldErrorMsg: () => '' } as any}
            value={sumBy(providerInfos, (providerInfo) => providerInfo.fees_count)}
            disabled
          />
          <CurrencyIntegerField
            title={t('titles.amount')}
            handleChange={console.error}
            value={Number(sumBy(providerInfos, (providerInfo) => providerInfo.amount).toFixed(2))}
            disabled
          />
          <PeriodField
            title={t('titles.time_range')}
            value={{
              from: stringToZonedDateTime(props.fees_scheme.start_date),
              to: stringToZonedDateTime(props.fees_scheme.end_date),
            }}
            handleChange={console.error}
            validation={{ isFieldValid: () => true, getFieldErrorMsg: () => '' } as any}
            disabled
          />
        </CardSection>
      </Card>
      {loading
        ? null
        : providerInfos.map((providerInfo: IProviderInfo) => (
            <FeeSummaryAccordion
              info={providerInfo}
              key={providerInfo.providerId}
              onClickExport={getProviderOnClickExport(providerInfo.providerId)}
              fees_scheme={props.fees_scheme}
            />
          ))}
    </>
  )
}

export default FeeSummaryPageList
